import { useContext, useEffect } from 'react';
import { UserContext } from '../context/authContext';

/**
 * useUnloadDocumentWithContextAuth
 *
 * Reads the current auth token from AuthContext via useAuth(),
 * then fires a keep-alive request (fetch with keepalive: true) on unload,
 * and a normal POST when the component unmounts normally.
 */
export function UseUnloadDocumentWithContextAuth(documentId: string) {
  const { token } = useContext(UserContext); // grab token from React Context

  
  useEffect(() => {
    // If there's no valid documentId, do nothing.
    if (!documentId) {
      return;
    }
    const url = `${process.env.REACT_APP_KORU_API}/document?type=removeLock`;
    const payload = {
      documentId
    };
    const bodyString = JSON.stringify(payload);

    const handleBeforeUnload = () => {
      // If you need Authorization header, use fetch with keepalive:
      if (token) {
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: bodyString,
          keepalive: true
        }).catch(() => {
          /* best-effort; swallow errors */
        });
      } else {
        // no token—still send best-effort without Auth header
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: bodyString,
          keepalive: true
        }).catch(() => {
          /* best-effort */
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Component unmounts normally—do a regular fetch POST (no keepalive needed)
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      fetch(url, {
        method: 'POST',
        headers,
        body: bodyString
      }).catch(() => {
        /* log or ignore */
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);
}
